import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { formatDate } from '@folio/lib/date-formatting';
import { formatPrice } from '@folio/lib/number-formatting';
import { useAccessiblePayerCustomerIds } from '@folio/hooks/useAccessiblePayerCustomerIds';
import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import {
  customerControllerGetCustomerQueryOptions,
  customerControllerGetInvoicingScheduleQueryOptions,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  type CustomerGetInvoicingScheduleOutput,
} from 'korint-sdk';
import { useAssertedParams } from '@folio/lib';
import { useTranslation } from 'react-i18next';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

type InvoiceScheduleCompareModuleProps = {
  branches: Array<string | undefined>;
};

export function InvoicesScheduleCompareModule({ branches }: InvoiceScheduleCompareModuleProps) {
  const { policyId } = useAssertedParams();
  const { data: policy } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId: branches[branches.length - 1] }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });
  const payerCustomerIds = useAccessiblePayerCustomerIds(policy);

  return (
    <Stack>
      {payerCustomerIds.map(customerId => (
        <InvoicesScheduleByCustomerCompareModule
          key={customerId}
          customerId={customerId}
          branches={branches}
          showCustomerId={payerCustomerIds.length > 1}
        />
      ))}
    </Stack>
  );
}

type InvoicesScheduleByCustomerCompareModuleProps = {
  customerId: string;
  branches: Array<string | undefined>;
  showCustomerId?: boolean;
};

function InvoicesScheduleByCustomerCompareModule({
  customerId,
  branches,
  showCustomerId = false,
}: InvoicesScheduleByCustomerCompareModuleProps) {
  const { t } = useTranslation();
  const customerInformations = usePolicyProductConfig('customerInformations');

  // Get the last branch (typically the working/new branch) for customer display
  const branchId = branches[branches.length - 1];

  const { data: customer } = useSuspenseQuery({
    ...customerControllerGetCustomerQueryOptions(customerId, {
      branchId,
    }),
  });

  const invoicingScheduleResults = useSuspenseQueries({
    queries: branches.map(branchId => ({
      ...customerControllerGetInvoicingScheduleQueryOptions(customerId, { branchId }),
      select: (d: CustomerGetInvoicingScheduleOutput) => d.data,
    })),
  });

  const allInvoiceSchedules = invoicingScheduleResults
    .filter(result => result.isSuccess && result.data)
    .flatMap(result => result.data || []);

  // Get unique dates from issuedAt
  const uniqueDates = Array.from(
    new Set(allInvoiceSchedules.map(schedule => schedule.issuedAt)),
  ).sort();

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('invoicesSchedule.module.title')}
          {showCustomerId && (
            <Text inline size="sm" weight="normal" tone="muted" className="ml-2">
              (
              {customerDisplayName(
                customer ? [customer] : [],
                customerInformations?.fieldsConfig,
              ) || customerId}
              )
            </Text>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table className="[&_tr]:border-0 table-fixed">
          <TableHeader className="[&_tr]:border-0">
            <TableRow>
              <TableHead>{t('invoicesSchedule.compare.nameColumn')}</TableHead>
              {branches.map((branch, index) => (
                <TableHead key={branch || index}>
                  {index === 0 && branches.length > 1
                    ? t('invoicesSchedule.compare.previousAmount')
                    : t('invoicesSchedule.compare.newAmount')}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {uniqueDates.map(issuedAt => {
              // Get all amounts for this date across branches
              const amountsForDate = invoicingScheduleResults.map(result => {
                const scheduleForBranch = result.data?.find(
                  schedule => schedule.issuedAt === issuedAt,
                );
                return scheduleForBranch?.total.amountWithFeesAndTaxes;
              });

              // Check if values are different between branches
              const hasValuesAvailable = amountsForDate.some(amount => amount !== undefined);
              const hasDifferentValues =
                hasValuesAvailable &&
                amountsForDate.filter(amount => amount !== undefined).length > 1 &&
                new Set(amountsForDate.filter(amount => amount !== undefined)).size > 1;

              return (
                <TableRow key={issuedAt} className={hasDifferentValues ? 'bg-slate-100' : ''}>
                  <TableCell>
                    {t('invoicesSchedule.compare.invoicePrefix')} - {formatDate(new Date(issuedAt))}
                  </TableCell>
                  {invoicingScheduleResults.map((result, branchIndex) => {
                    // Find the schedule for this date and branch
                    const scheduleForBranch = result.data?.find(
                      schedule => schedule.issuedAt === issuedAt,
                    );
                    return (
                      <TableCell key={issuedAt + branches[branchIndex]}>
                        {scheduleForBranch
                          ? formatPrice(scheduleForBranch.total.amountWithFeesAndTaxes)
                          : '-'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
